import { Alert, Badge, Group, Skeleton, Stack, Text } from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import {
  reflexPanelsService,
  type CasePanelSummaryOrder,
} from "../../services/reflexPanels.service";

interface PanelSummarySectionProps {
  caseId: string;
}

const SOURCE_LABELS: Record<CasePanelSummaryOrder["source"], string> = {
  accessioning: "Accessioning",
  reflex: "Reflex",
  pathologist_review: "Pathologist Review",
};

const BILLING_LABELS: Record<CasePanelSummaryOrder["billingMode"], string> = {
  group_only: "Group CPT only",
  group_plus_individual: "Group + selected members",
  individual_only: "Individual CPTs",
};

function formatComponent(
  component: CasePanelSummaryOrder["components"][number]
): string {
  const cpt = component.resolvedCpt?.code
    ? ` · Allocated CPT ${component.resolvedCpt.code}`
    : "";
  return `Block ${component.blockNumber}: ${component.stainCode} ×${component.quantity} · ${component.billingPolicy}${cpt}`;
}

export default function PanelSummarySection({
  caseId,
}: PanelSummarySectionProps) {
  const queryKey = useMemo(
    () => ["pathologist-review", caseId, "panels"] as const,
    [caseId]
  );
  const loadCasePanels = useCallback(
    () => reflexPanelsService.getCasePanels(caseId),
    [caseId]
  );
  const query = useQuery({
    queryKey,
    queryFn: loadCasePanels,
    enabled: Boolean(caseId),
    staleTime: 15_000,
  });

  if (query.isLoading) {
    return <Skeleton h={86} radius="md" aria-label="Loading panel orders" />;
  }
  if (query.isError) {
    return (
      <Alert
        color="red"
        icon={<IconAlertCircle size={18} />}
        title="Panels unavailable"
      >
        Panel and CPT context could not be loaded. Refresh the case before
        relying on its billing explanation.
      </Alert>
    );
  }
  if (!query.data?.length) return null;

  return (
    <Stack gap="md" aria-label="Panel orders">
      {query.data.map((specimen) => (
        <Stack key={specimen.specimenId} gap="xs">
          <Text size="xs" fw={700}>
            Specimen {specimen.specimenNumber}
          </Text>
          {specimen.orders.map((order) => (
            <Stack key={order.id} gap={4}>
              <Group justify="space-between" align="flex-start" wrap="nowrap">
                <Text size="xs" fw={600}>
                  {order.panelName} · v{order.versionNumber}
                </Text>
                <Badge size="xs" variant="light">
                  {order.status.replace(/_/g, " ")}
                </Badge>
              </Group>
              <Text size="xs" c="dimmed">
                {SOURCE_LABELS[order.source]}
                {order.operationalChildCase
                  ? ` · ${order.operationalChildCase.caseNumber}`
                  : " · Parent case"}
              </Text>
              <Text size="xs">
                {BILLING_LABELS[order.billingMode]}
                {order.groupCpt ? ` · Group CPT ${order.groupCpt.code}` : ""}
              </Text>
              {order.components.map((component) => (
                <Text key={component.id} size="xs" c="dimmed">
                  {formatComponent(component)} · {component.completedSlides}/
                  {component.totalSlides} complete
                </Text>
              ))}
            </Stack>
          ))}
        </Stack>
      ))}
    </Stack>
  );
}
